import { DiseaseRiskIcon, MycotoxinIcon } from '@protector/icons';
import type { Dictionary } from '@protector/utils/types';
import type { RangePickerValue } from 'antdV3/lib/date-picker/interface';
import { ReactComponent as AbioticStressIcon } from '@features/regional-overview/assets/models/abiotic-stress.svg';
import { ReactComponent as DigitalTrapsIcon } from '@features/regional-overview/assets/models/add-trap.svg';
import { ReactComponent as InoculumIcon } from '@features/regional-overview/assets/models/inoculum.svg';
import { COLOR_OPTIONS } from 'config/style';
import type { mapboxgl } from 'core/utils/map/mapbox-gl';
import type { Dayjs } from 'dayjs';
import type { Feature, Geometry } from 'geojson';
import {
  RegionalOverviewSource,
  type RegionalOverviewProperties,
  type RegionalOverviewV2Properties
} from 'querys/regional-overview/regional-overview.query.models';
import type { ReactElement, ReactNode } from 'react';

import { AbioticInfoModal } from './abiotic-stress/info-modal';
import { buildBloomingFilter } from './components/filters/builders/blooming.builder';
import { buildClassFilter } from './components/filters/builders/class.builder';
import { buildCropsFilter } from './components/filters/builders/crops.builder';
import { buildDiseaseFilter } from './components/filters/builders/disease.builder';
import { buildGrowthStagesFilter } from './components/filters/builders/growth-stages.builder';
import { buildMycotoxinRiskFilter } from './components/filters/builders/mycotoxin-risk.builder';
import { buildPreviousCropFilter } from './components/filters/builders/previous-crop.builder';
import { buildRiskLevelFilter } from './components/filters/builders/risk-level.builder';
import { buildSowingDateFilter } from './components/filters/builders/sowing-date.builder';
import { buildTechnicianFilter } from './components/filters/builders/technician.builder';
import { buildTillageFilter } from './components/filters/builders/tillage.builder';
import { buildTrapGroupsFilter } from './components/filters/builders/trap-groups.builder';
import { buildVarietyFilter } from './components/filters/builders/variety.builder';
import { buildZonesFilter } from './components/filters/builders/zones.builder';
import type { OnRegionalFilterChange } from './components/regional-risk-filter-select/use-filter-changed';
import { InoculumInfo } from './inoculum-survival/inoculum-info.component';
import DiseaseRiskInfoModal from './regional-risk/info-modal/info-modal.component';

export interface RegionalOverviewLevel {
  level: number;
  type: string;
  value?: string;
  legend_color?: string;
  text_color?: string;
}

export interface IRegionalRiskFilter<T extends string> {
  filter: T;
  disabled?: boolean;
  disabledTooltip?: string;
  onFilterChanged?: OnRegionalFilterChange<T, FilterValue>;
  selectedValue: FilterValue;
  width: number;
  items?: unknown[];
  dataTestID?: string;
  title?: string;
}

export interface IRegionalOverviewFilterOption {
  key: string;
  value: string;
  title: string;
  icon?: ReactElement;
  quantity?: number;
}

export interface IRegionalOverviewMultiSelectValues {
  key: string;
  title: string;
  value?: string;
  icon?: ReactElement;
  children: IRegionalOverviewFilterOption[];
}

export interface IRegionalRiskPopupProperty {
  amountFields: number;
  icon: string;
  id: string;
  propertyName: string;
}

export enum RegionalOverviewFilterType {
  TECHNICIAN = 'technician',
  CROPS = 'crops',
  VARIETY = 'variety',
  DISEASE = 'disease',
  GROWTH_STAGES = 'growth_stages',
  SOWING_DATE = 'sowingDate',
  RISK_LEVEL = 'risk_level',
  GROUPS = 'groups',
  ZONES = 'zones',
  PREVIOUS_CROP = 'previous_crop',
  TILLAGE = 'tillage',
  CLASS = 'class',
  BLOOMING = 'blooming',
  MYCOTOXIN_RISK = 'mycotoxin_risk',
  TEXT_SEARCH = 'text_search'
}

export type DateRangeFilterValue = [Dayjs, Dayjs] | undefined;
export type FilterValue = RangePickerValue | string | string[] | DateRangeFilterValue | undefined;
export type FilterValueDictionary = Dictionary<FilterValue>;

export type MobileView = 'map' | 'list';

export interface RegionalOverviewConfiguration {
  title: string;
  icon?: ReactElement;
  defaultModel: RegionalOverviewSource;
  items: RegionalOverviewConfigurationItem[];
}

interface ModalConfiguration {
  component: ReactNode;
  title: string;
}

export interface RegionalOverviewConfigurationItem {
  key?: string;
  model: RegionalOverviewSource;
  title?: string;
  icon?: ReactElement;
  showDetails?: boolean;
  showTotalAreas?: boolean;
  enabledFilters: Filter[];
  mapControls: MapControlsConfiguration;
  modal?: ModalConfiguration;
}

export const ALL_FILTER_VALUE = 'all';

export interface FilterOption {
  value: string;
  labelKey: string;
  meta?: Record<string, unknown>;
}

export type FilterOptionsDictionary = Partial<Record<RegionalOverviewFilterType, FilterOption[]>>;

export interface Filter<P = unknown> {
  type: RegionalOverviewFilterType;
  dependsOn?: RegionalOverviewFilterType[];
  defaultValue?: FilterValue;
  includeAllOption?: boolean;
  translatable?: boolean;
  freeform?: boolean;
  sortOptions?: (options: FilterOption[]) => FilterOption[];
  getValues: (feature: Feature<Geometry, P>, context?: Record<string, unknown>) => string[];
  matches: (
    feature: Feature<Geometry, P>,
    value: FilterValue,
    context?: Record<string, unknown>,
    appliedValues?: FilterValueDictionary
  ) => boolean;
  prepare?: (feature: Feature<Geometry, P>, value: FilterValue, context?: Record<string, unknown>) => Feature<Geometry, P>;
}

export const buildFilter = <P,>(spec: Filter<P>): Filter => spec as unknown as Filter;

export interface MapLegendLevel {
  type: string;
  value?: string;
  level: number;
  color: string;
  label: string;
  prominent?: boolean;
}

export interface MapLegendConfiguration {
  titleKey: string;
  groups?: string[];
  levels: MapLegendLevel[];
}

export interface MapControlsConfiguration {
  showCenter: boolean;
  showChangeMode: boolean;
  showZoom: boolean;
  showLegend: boolean;
  legend?: MapLegendConfiguration;
  timeline?: { pastDays: number; futureDays: number };
}

export { RegionalOverviewFilterType as FilterType };

export const regionalOverviewConfigurations: RegionalOverviewConfiguration[] = [
  {
    title: 'pages.regional_overview.disease_risk.title',
    defaultModel: RegionalOverviewSource.DISEASE_RISK,
    icon: <DiseaseRiskIcon fill={COLOR_OPTIONS.ORANGE_50} width={18} height={18} />,
    items: [
      {
        modal: {
          component: <DiseaseRiskInfoModal />,
          title: 'pages.regional_overview.disease_risk.info_modal.title'
        },
        model: RegionalOverviewSource.DISEASE_RISK,
        showDetails: true,
        showTotalAreas: true,
        mapControls: {
          showCenter: true,
          showChangeMode: true,
          showZoom: true,
          showLegend: true,
          timeline: { pastDays: 5, futureDays: 10 }
        },
        enabledFilters: [
          buildRiskLevelFilter(),
          buildCropsFilter('all', true),
          buildTechnicianFilter(),
          buildGrowthStagesFilter(),
          buildDiseaseFilter(),
          buildVarietyFilter((properties: RegionalOverviewV2Properties) => properties.values?.variety_name),
          buildSowingDateFilter()
        ]
      },
      {
        model: RegionalOverviewSource.GRID_VIEW,
        showDetails: true,
        showTotalAreas: false,
        mapControls: {
          showCenter: false,
          showChangeMode: false,
          showZoom: false,
          showLegend: true,
          timeline: { pastDays: 5, futureDays: 10 }
        },
        enabledFilters: [buildCropsFilter('wheat', true), buildGrowthStagesFilter(), buildDiseaseFilter()]
      }
    ]
  },
  {
    title: 'pages.regional_overview.mycotoxin_risk.title',
    icon: <MycotoxinIcon fill={COLOR_OPTIONS.RED_80} width={18} height={18} />,
    defaultModel: RegionalOverviewSource.QUALIMETRE,
    items: [
      {
        title: 'pages.regional_overview.mycotoxin_risk.title',
        model: RegionalOverviewSource.QUALIMETRE,
        showDetails: true,
        showTotalAreas: true,
        mapControls: {
          showCenter: true,
          showChangeMode: true,
          showZoom: true,
          showLegend: true,
          timeline: { pastDays: 5, futureDays: 10 }
        },
        enabledFilters: [
          buildZonesFilter(),
          buildVarietyFilter((properties: RegionalOverviewProperties) => properties.variety),
          buildPreviousCropFilter(),
          buildTillageFilter(),
          buildClassFilter(),
          buildBloomingFilter(),
          buildMycotoxinRiskFilter()
        ]
      }
    ]
  },
  {
    title: 'pages.regional_overview.digital_traps.title',
    defaultModel: RegionalOverviewSource.DIGITAL_TRAPS,
    icon: <DigitalTrapsIcon width={18} height={18} />,
    items: [
      {
        model: RegionalOverviewSource.DIGITAL_TRAPS,
        showDetails: true,
        showTotalAreas: true,
        mapControls: {
          showCenter: true,
          showChangeMode: true,
          showZoom: true,
          showLegend: true
        },
        enabledFilters: [buildTrapGroupsFilter()]
      }
    ]
  },
  {
    title: 'pages.regional_overview.abiotic_stress.title',
    defaultModel: RegionalOverviewSource.ABIOTIC_STRESS,
    icon: <AbioticStressIcon width={18} height={18} />,
    items: [
      {
        modal: {
          component: <AbioticInfoModal />,
          title: 'pages.regional_overview.abiotic_stress.info_modal.title'
        },
        model: RegionalOverviewSource.ABIOTIC_STRESS,
        showDetails: true,
        showTotalAreas: false,
        mapControls: {
          showCenter: true,
          showChangeMode: true,
          showZoom: true,
          showLegend: true
        },
        enabledFilters: [buildCropsFilter('winter_wheat', false)]
      }
    ]
  },
  {
    title: 'pages.regional_overview.inoculum_survival.title',
    defaultModel: RegionalOverviewSource.INOCULUM_SURVIVAL,
    icon: <InoculumIcon fill={COLOR_OPTIONS.YELLOW_50} width={18} height={18} />,
    items: [
      {
        modal: {
          component: <InoculumInfo />,
          title: 'pages.regional_overview.inoculum_survival.info_moda.title'
        },
        model: RegionalOverviewSource.INOCULUM_SURVIVAL,
        showDetails: true,
        showTotalAreas: false,
        mapControls: {
          showCenter: true,
          showChangeMode: true,
          showZoom: true,
          showLegend: true
        },
        enabledFilters: [buildDiseaseFilter()]
      }
    ]
  }
];

export const MAP_INITIAL_POSITION: mapboxgl.LngLatLike = [2.3488, 48.85341];

export const routeToRegionalOverviewSourceDic: Record<string, RegionalOverviewSource> = {
  'digital-traps': RegionalOverviewSource.DIGITAL_TRAPS,
  'disease-risk': RegionalOverviewSource.DISEASE_RISK,
  'grid-view': RegionalOverviewSource.GRID_VIEW,
  mycotoxin: RegionalOverviewSource.QUALIMETRE,
  'inoculum-survival': RegionalOverviewSource.INOCULUM_SURVIVAL,
  'abiotic-stress': RegionalOverviewSource.ABIOTIC_STRESS
};

export const regionalOverviewSourceToRouteDic: Record<RegionalOverviewSource, string> = {
  [RegionalOverviewSource.DIGITAL_TRAPS]: 'digital-traps',
  [RegionalOverviewSource.DISEASE_RISK]: 'disease-risk',
  [RegionalOverviewSource.GRID_VIEW]: 'grid-view',
  [RegionalOverviewSource.QUALIMETRE]: 'mycotoxin',
  [RegionalOverviewSource.INOCULUM_SURVIVAL]: 'inoculum-survival',
  [RegionalOverviewSource.ABIOTIC_STRESS]: 'abiotic-stress'
};

export const DEFAULT_REGIONAL_OVERVIEW_MODEL = regionalOverviewSourceToRouteDic[regionalOverviewConfigurations[0].items[0].model];
